import type { Match } from '@uefadigital/panenka';
import { PkCarousel } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { useGetMatchEventsQuery } from 'api/fspApi';
import { SubstitutionCard } from 'components/SubstitutionCard';
import type { MatchEvents } from 'types/ApiResponseInterfaces';
import type { ConfigCompetition } from 'utils/config';

import * as style from './style.module.css';

const breakpoints = {
  0: { slidesPerView: 1.2, slidesPerGroup: 1, spaceBetween: 16 },
  400: { slidesPerView: 1.5, slidesPerGroup: 1, spaceBetween: 16 },
  600: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 20 },
  800: { slidesPerView: 2.5, slidesPerGroup: 1, spaceBetween: 40 },
  960: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 40 },
  1440: { slidesPerView: 3.8, slidesPerGroup: 1, spaceBetween: 40 },
} as const;

interface SubstitutionsData {
  matchData: Match;
  isDesktop: boolean;
  isPDFPrint: boolean;
  competition?: ConfigCompetition;
}

export const Substitutions = ({ matchData, isDesktop, isPDFPrint = false, competition }: SubstitutionsData) => {
  const { t } = useTranslation();

  const {
    data: lineUpEventData = [],
    isLoading: lineUpEventDataLoading,
    isError: lineUpEventDataError,
  } = useGetMatchEventsQuery({
    matchId: matchData.id,
    filter: 'LINEUP',
    offset: 0,
    limit: 100,
  });

  if (lineUpEventDataLoading || lineUpEventDataError || !lineUpEventData.length) return <div />;

  const substitutionsEvents = lineUpEventData
    .filter((el) => el.type === 'SUBSTITUTION')
    .sort((substitution, nextSubstitution) => {
      return new Date(substitution.timestamp).getTime() - new Date(nextSubstitution.timestamp).getTime();
    });

  const showSubstitutions = (substitutions: Array<MatchEvents>) =>
    substitutions.map((substitution, i) => (
      <SubstitutionCard competition={competition} key={i} substitution={substitution} />
    ));

  const showPrintSubstitutions = (substitutions: Array<MatchEvents>) =>
    substitutions.map((substitution, i) => (
      <div className="pk-my--xs" key={i}>
        <SubstitutionCard key={i} substitution={substitution} />
      </div>
    ));

  if (isPDFPrint) {
    return (
      <div className="pk-m--0 pk-p--0">
        <div
          slot="headline"
          className={`${
            isDesktop ? 'pk-font-size--xl2 pk-line-height--sm pr-font-size--16 pr-font-weight--500' : 'pk-font-size--xl'
          } `}
        >
          {t('substitutions')}
        </div>
        <div className={clsx('pk-d--grid pk-gap--s', style.substitutionsGrid)}>
          {showPrintSubstitutions(substitutionsEvents)}
        </div>
      </div>
    );
  }

  return (
    <PkCarousel boxShadowPadding autoHeight breakpoints={breakpoints}>
      <div slot="headline" className={`${isDesktop ? 'pk-font-size--xl2 pk-line-height--sm' : 'pk-font-size--xl'} `}>
        {t('substitutions')}
      </div>
      {showSubstitutions(substitutionsEvents)}
    </PkCarousel>
  );
};
